import { BaseTextFieldProps, CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete, RenderInputParams, UseAutocompleteProps } from '@material-ui/lab';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import gql from 'graphql-tag';
import React from 'react';
import { useQuery } from 'react-apollo';

interface BrandSelectorProps<
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined
  > extends Pick<BaseTextFieldProps, 'label' | 'variant' | 'fullWidth' | 'margin' | 'required' | 'error' | 'size' | 'helperText'>, Omit<UseAutocompleteProps<Brand, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'renderOption' | 'options' | 'getOptionLabel'> {
    
}

export function BrandSelector<Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, FreeSolo extends boolean | undefined>(props: BrandSelectorProps<Multiple, DisableClearable, FreeSolo>) {
    
    const brandQuery = useQuery<BrandsQueryData>(BrandsQuery, {
        fetchPolicy: 'cache-first',
    });
    
    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
            return {
                ...params.InputProps,
                endAdornment: (
                    <React.Fragment>
                        {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                        {params.InputProps.endAdornment}
                    </React.Fragment>
                ),
            };
        };
        
    const { label, variant, fullWidth, margin, required, error, size, multiple, value, helperText, ...autocompleteProps } = props;

    const normalizedValue = multiple ? (Array.isArray(value) ? value : []) : value;

    return (
        <Autocomplete<Brand, Multiple, DisableClearable, FreeSolo>
            {...autocompleteProps}
            multiple={multiple}
            value={normalizedValue as typeof value}
            options={brandQuery.data?.administrator.brands.brand || []}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    fullWidth={true}
                    margin="none"
                    required={required}
                    InputProps={getInputProps(params, brandQuery.loading)}
                    error={error}
                    size={size}
                    helperText={helperText}
                />
            )}
            // tslint:disable-next-line: jsx-no-lambda
            renderOption={(option, { inputValue }) => {
                const matches = match(option.name, inputValue);
                const parts = parse(option.name, matches);

                return (
                    <div>
                        {parts.map((part, index) => (
                            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                            </span>
                        ))}
                    </div>
                );
            }}
        />
    )
}

export interface BrandsQueryData {
    administrator: Administrator;
}

export interface Administrator {
    brands:     Brands;
}

export interface Brands {
    brand:      Brand[];
}

export interface Brand {
    id:         number;
    logoID:     null | string;
    logoUrl:    string;
    name:       string;
    themeID:    null | string;
    title:      string;
}


export const BrandsQuery = gql`
    query brands {
        administrator {
            brands {
                brand {
                    id
                    logoID
                    logoUrl
                    name
                    themeID
                    title
                }
            }
        }
    }
`;